import type { ReactNode } from "react"
import {
	Bar,
	BarChart,
	CartesianGrid,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import type { ReportingTooltipTable } from "./chart-tooltip"
import type { ReportingSeries } from "./theme"
import { ReportingChartFrame } from "./chart-frame"
import { ReportingChartTooltip } from "./chart-tooltip"
import {
	REPORTING_X_AXIS_HEIGHT,
	reportingBaselineClassName,
	reportingChartTheme,
	reportingGridClassName,
	toneColor,
} from "./theme"
import { useReportingBarHoverCursor } from "./use-dark-mode"

export interface ReportingBarPoint {
	date: string
	[key: string]: string | number | Record<string, number> | undefined
}

interface ReportingBarChartProps {
	title: string
	description?: string
	data: ReportingBarPoint[]
	series: ReportingSeries[]
	isLoading?: boolean
	average?: number
	averageLabel?: string
	// Sits in the header, right of the title. Takes the place of the average.
	legend?: ReactNode
	tooltipTable?: ReportingTooltipTable
	compactTooltip?: boolean
	stacked?: boolean
	className?: string
	height?: number
	categoryAxis?: boolean
	flush?: boolean
}

export const ReportingBarChart = ({
	title,
	description,
	data,
	series,
	isLoading,
	average,
	averageLabel = "Average",
	legend,
	tooltipTable,
	compactTooltip = false,
	stacked,
	className,
	height = 200,
	categoryAxis = false,
	flush,
}: ReportingBarChartProps) => {
	const isEmpty = data.length === 0
	const hoverCursor = useReportingBarHoverCursor()
	const chartHeight = categoryAxis ? Math.max(height, 240) : height

	return (
		<ReportingChartFrame
			title={title}
			description={description}
			isLoading={isLoading}
			isEmpty={isEmpty}
			className={className}
			flush={flush}
			legend={
				legend ??
				(average !== undefined ? (
					<span className="text-xs text-tertiary">
						{averageLabel} {Math.round(average)}
					</span>
				) : undefined)
			}
		>
			<div
				className="w-full [&_*]:outline-none"
				style={{ height: chartHeight }}
			>
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={data}
						margin={{
							top: 8,
							right: 8,
							left: 0,
							bottom: categoryAxis ? 28 : 0,
						}}
					>
						<CartesianGrid
							className={reportingGridClassName}
							vertical={false}
						/>
						<ReferenceLine y={0} className={reportingBaselineClassName} />
						<XAxis
							dataKey="date"
							tickLine={false}
							axisLine={false}
							interval={categoryAxis ? 0 : "preserveStartEnd"}
							angle={categoryAxis ? -30 : 0}
							textAnchor={categoryAxis ? "end" : "middle"}
							height={categoryAxis ? 56 : REPORTING_X_AXIS_HEIGHT}
							tick={{ fontSize: 11 }}
							className="text-xs text-gray-500 dark:text-gray-400"
						/>
						<YAxis
							orientation="right"
							tickLine={false}
							axisLine={false}
							className="text-xs text-gray-500 dark:text-gray-400"
							width={32}
						/>
						<Tooltip
							cursor={hoverCursor}
							content={
								<ReportingChartTooltip
									table={tooltipTable}
									compact={compactTooltip}
								/>
							}
							offset={compactTooltip ? 16 : undefined}
							wrapperStyle={compactTooltip ? { zIndex: 20 } : undefined}
						/>
						{average !== undefined ? (
							<ReferenceLine
								y={average}
								stroke={reportingChartTheme.muted}
								strokeDasharray="4 4"
							/>
						) : null}
						{series.map((s, index) => (
							<Bar
								key={s.key}
								dataKey={s.key}
								name={s.label}
								stackId={stacked ? "stack" : undefined}
								fill={toneColor(s.tone)}
								maxBarSize={reportingChartTheme.maxBarSize}
								radius={
									stacked && index === series.length - 1
										? [
												reportingChartTheme.barRadius,
												reportingChartTheme.barRadius,
												0,
												0,
											]
										: stacked
											? 0
											: [
													reportingChartTheme.barRadius,
													reportingChartTheme.barRadius,
													0,
													0,
												]
								}
								isAnimationActive={false}
							/>
						))}
					</BarChart>
				</ResponsiveContainer>
			</div>
		</ReportingChartFrame>
	)
}
